import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { Bug, Check, Lightbulb, Wrench } from "lucide-react";
import { auth } from "../firebase";
import { FormDialog, Field } from "./dreamShared";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import { MELT_BUTTON, MELT_CHIP, MELT_CHIP_ON, MELT_DIALOG, MELT_INPUT, MELT_LABEL, MELT_TEXTAREA } from "./ui/melt";
import { cn } from "../lib/utils";
import { ease } from "../lib/motion";

// "Send feedback" from the sidebar account menu: a bug, a new feature, or an
// improvement to something that exists. The backend saves it (the top-level
// `feedback` collection) and emails it to us (backend/feedback.py).

const API_BASE = process.env.REACT_APP_API_URL ?? "http://localhost:5050";
/**
 * How long Send waits before thanking them anyway. A stopped backend takes
 * ~15s to start; the request keeps going after the dialog closes.
 */
const THANKS_AFTER_MS = 2500;

export type FeedbackKind = "bug" | "feature" | "improvement";

const KINDS = [
  { value: "bug", label: "Bug", icon: Bug },
  { value: "feature", label: "Feature", icon: Lightbulb },
  { value: "improvement", label: "Improvement", icon: Wrench },
] as const;

const COPY: Record<FeedbackKind, { main: string; placeholder: string; thanks: string }> = {
  bug: {
    main: "What happened?",
    placeholder: "What you clicked, what you saw…",
    thanks: "We'll look into it and follow up by email if we need more detail.",
  },
  feature: {
    main: "Your idea",
    placeholder: "e.g. Remind me to follow up after an interview",
    thanks: "Every request gets read. We'll let you know if it ships.",
  },
  improvement: {
    main: "What could be better?",
    placeholder: "Something that's slow, confusing, or takes too many clicks…",
    thanks: "Every suggestion gets read. We'll let you know when it's better.",
  },
};

const AREAS = ["Applications", "Dream companies", "Networking", "Drafts", "Career", "Settings", "Other"];

const AREA_BY_ROUTE: [string, string][] = [
  ["/dashboard/tracker", "Applications"],
  ["/dashboard/dreamcompanies", "Dream companies"],
  ["/dashboard/networking", "Networking"],
  ["/dashboard/coffeechats", "Networking"],
  ["/dashboard/applications", "Drafts"],
  ["/dashboard/prep", "Interview prep"],
  ["/settings", "Settings"],
];

const SEVERITIES = ["Minor", "Annoying", "Blocking"];
const IMPACTS = ["Nice to have", "Would use it often", "Can't live without"];

function ChipRow({ options, value, onChange }: { options: string[]; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(value === opt ? "" : opt)}
          aria-pressed={value === opt}
          className={cn(MELT_CHIP, value === opt && MELT_CHIP_ON)}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

/** Animates its height to fit the content, so switching type doesn't jump. */
function AutoHeight({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | "auto">("auto");

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setHeight(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Padding + negative margin leave room for input focus rings inside overflow-hidden.
  return (
    <motion.div
      initial={false}
      animate={{ height: height === "auto" ? "auto" : height + 8 }}
      transition={{ duration: 0.3, ease }}
      className="-m-1 overflow-hidden p-1"
    >
      <div ref={ref}>{children}</div>
    </motion.div>
  );
}

type FeedbackDialogProps = {
  open: boolean;
  plan?: string;
  /** The tab it opens on (default: bug) */
  startKind?: FeedbackKind;
  onClose: () => void;
};

export default function FeedbackDialog({ open, plan, startKind = "bug", onClose }: FeedbackDialogProps) {
  const location = useLocation();
  const [kind, setKind] = useState<FeedbackKind>("bug");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [main, setMain] = useState("");
  const [expected, setExpected] = useState(""); // bug
  const [problem, setProblem] = useState(""); // feature
  const [area, setArea] = useState(""); // bug + improvement
  const [severity, setSeverity] = useState(""); // bug
  const [impact, setImpact] = useState(""); // feature + improvement

  useEffect(() => {
    if (!open) return;
    setKind(startKind);
    setSent(false);
    setSubmitting(false);
    setError("");
    setMain("");
    setExpected("");
    setProblem("");
    setArea(AREA_BY_ROUTE.find(([route]) => location.pathname.startsWith(route))?.[1] ?? "");
    setSeverity("");
    setImpact("");
    // Only reset when the dialog opens, not when the route changes underneath it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const copy = COPY[kind];

  const handleSubmit = async () => {
    const user = auth.currentUser;
    if (!user) return;
    setSubmitting(true);
    setError("");
    const send = async () => {
      const response = await fetch(`${API_BASE}/feedback`, {
        method: "POST",
        headers: { Authorization: `Bearer ${await user.getIdToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          kind,
          message: main.trim(),
          ...(kind === "bug" && { expected: expected.trim(), area, severity }),
          ...(kind === "feature" && { problem: problem.trim(), impact }),
          ...(kind === "improvement" && { area, impact }),
          plan: plan ?? "",
          route: location.pathname,
          userAgent: navigator.userAgent,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
        }),
      });
      if (!response.ok) throw new Error(`Feedback failed: ${response.status}`);
    };
    try {
      const request = send();
      // A late failure can only be logged: the dialog has thanked them
      request.catch((err) => console.error("Failed to send feedback", err));
      await Promise.race([request, new Promise((resolve) => setTimeout(resolve, THANKS_AFTER_MS))]);
      setSent(true);
    } catch (err) {
      console.error("Failed to send feedback", err);
      setError("Couldn't send that. Try again, or email help@oncaramel.com.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  if (sent) {
    return (
      <Dialog open onOpenChange={(next) => !next && onClose()}>
        <DialogContent showClose={false} className={cn(MELT_DIALOG, "max-w-[400px] px-7 py-8 text-center")}>
          <span className="mx-auto grid h-11 w-11 place-items-center rounded-full bg-melt-peach text-melt-caramel">
            <Check size={22} strokeWidth={2} />
          </span>
          <DialogTitle className="mt-4 text-[20px] font-semibold tracking-[-.02em] text-melt-text1">
            Thanks, we got it
          </DialogTitle>
          <DialogDescription className="mt-1.5 text-sm text-melt-text2">{copy.thanks}</DialogDescription>
          <button type="button" onClick={onClose} className={cn(MELT_BUTTON, "mt-6 w-full")}>
            Done
          </button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <FormDialog
      open
      onClose={onClose}
      title="Send feedback"
      description="Found a bug or have an idea? We read everything."
      submitLabel={submitting ? "Sending…" : "Send"}
      canSubmit={Boolean(main.trim())}
      submitting={submitting}
      onSubmit={handleSubmit}
      className="max-w-[680px]"
    >
      <div className="flex flex-col gap-2">
        <span className={MELT_LABEL}>What kind of feedback?</span>
        <div className="grid grid-cols-3 gap-1 rounded-xl bg-melt-text1/[.05] p-1">
          {KINDS.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => setKind(value)}
              aria-pressed={kind === value}
              className={cn(
                "flex h-9 items-center justify-center gap-2 rounded-[9px] text-sm font-medium transition-[background-color,color,box-shadow] duration-200",
                kind === value
                  ? "bg-white text-melt-text1 shadow-[0_1px_3px_rgba(92,52,23,.12)]"
                  : "text-melt-text2 hover:text-melt-text1"
              )}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </div>
      </div>

      <AutoHeight>
        <motion.div
          key={kind}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.22, ease }}
          className="flex flex-col gap-5"
        >
          <div className={cn("grid grid-cols-1 gap-5", kind === "bug" && "sm:grid-cols-2")}>
            <Field id="fb-main" label={copy.main} required>
              {kind === "feature" ? (
                <input
                  id="fb-main"
                  value={main}
                  onChange={(e) => setMain(e.target.value)}
                  placeholder={copy.placeholder}
                  className={MELT_INPUT}
                  autoFocus
                />
              ) : (
                <textarea
                  id="fb-main"
                  value={main}
                  onChange={(e) => setMain(e.target.value)}
                  placeholder={copy.placeholder}
                  rows={3}
                  className={MELT_TEXTAREA}
                  autoFocus
                />
              )}
            </Field>

            {kind === "bug" && (
              <Field id="fb-expected" label="What did you expect?">
                <textarea
                  id="fb-expected"
                  value={expected}
                  onChange={(e) => setExpected(e.target.value)}
                  rows={3}
                  className={MELT_TEXTAREA}
                />
              </Field>
            )}
          </div>

          {kind === "feature" && (
            <Field id="fb-problem" label="What problem would this solve?">
              <textarea
                id="fb-problem"
                value={problem}
                onChange={(e) => setProblem(e.target.value)}
                placeholder="What you're trying to do today and where it gets in the way…"
                rows={3}
                className={MELT_TEXTAREA}
              />
            </Field>
          )}

          {kind !== "feature" && (
            <div className="flex flex-col gap-2">
              <span className={MELT_LABEL}>Where?</span>
              <ChipRow options={AREAS} value={area} onChange={setArea} />
            </div>
          )}

          {kind === "bug" ? (
            <div className="flex flex-col gap-2">
              <span className={MELT_LABEL}>How bad is it?</span>
              <ChipRow options={SEVERITIES} value={severity} onChange={setSeverity} />
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <span className={MELT_LABEL}>How much would this help?</span>
              <ChipRow options={IMPACTS} value={impact} onChange={setImpact} />
            </div>
          )}
        </motion.div>
      </AutoHeight>

      {error && <p className="-mt-1 text-[13px] text-[#A5492E]">{error}</p>}
    </FormDialog>
  );
}
